import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { IMainMenuSectionView, IMainMenuViewOption } from '@rbi-ctg/menu';
import { useEffectOnce } from 'hooks/use-effect-once';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { useRecentItemsAvailable } from 'hooks/use-recent-items-available';
import { ITabbedMenuData } from 'hooks/use-tabbed-menu-header/types';
import {
  excludeDisabledMenuSections,
  filterMenuDataUsingActiveDayParts,
  separateBreakfastMenu,
} from 'pages/main-menu/utils';
import { useDayPartContext } from 'state/day-part';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS } from 'state/main-menu/types';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { getMenuOptionUrl } from 'utils/menu';
import {
  isFavoritesRoute,
  isItemPickerOrComboRoute,
  isRecentItemsRoute,
  relativeRoutes,
  routes,
} from 'utils/routing';

import { IGetTabbedMenuData, IuseTabbedHeaderProps } from './types';

function concatValidItems<T>(dataToConcat: (T | null)[], data: T[]): T[] {
  const items = dataToConcat.filter((item): item is T => {
    return item !== null;
  });

  return items.concat(data);
}

function getTabbedMenuData({
  dayPartMenu,
  mainMenuItems,
  activeDayParts = [],
  showBurgersForBreakfast,
  serviceMode,
  showStaticMenu,
  enableDaypartsMergedMenu,
}: IGetTabbedMenuData) {
  const activeMenuOptions = activeDayParts?.length
    ? excludeDisabledMenuSections({
        menuData: mainMenuItems,
        activeDayParts,
        showBurgersForBreakfast,
        serviceMode,
        showStaticMenu,
      }) || []
    : mainMenuItems;

  if ((dayPartMenu === DAY_PART_SELECTIONS.ALL_KEY || !dayPartMenu) && !enableDaypartsMergedMenu) {
    return activeMenuOptions;
  }

  if (enableDaypartsMergedMenu) {
    const { menuData } = filterMenuDataUsingActiveDayParts(activeMenuOptions, activeDayParts);
    return menuData;
  } else {
    const { menuData, breakfastMenuData } = separateBreakfastMenu(activeMenuOptions);

    const shouldRenderBreakfast =
      !!breakfastMenuData?.length && dayPartMenu === DAY_PART_SELECTIONS.BREAKFAST_KEY;
    return shouldRenderBreakfast ? breakfastMenuData : menuData;
  }
}

export const useTabbedMenuHeader = ({ dayPartMenu, activeDayParts }: IuseTabbedHeaderProps) => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  const recentItemsAvailable = useRecentItemsAvailable();
  const enableRecentItems = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_IN_MENU);
  const enableTabbedMenuBarRecents = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR_RECENTS);
  const enableTabbedMenuBarFavorites = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR_FAVORITES);
  const enabledDirectAccessToMenu = useFlag(LaunchDarklyFlag.ENABLE_DIRECT_ACCESS_TO_MENU);
  const enableDaypartsMergedMenu = useFlag(LaunchDarklyFlag.ENABLE_DAYPARTS_MERGED_MENU);

  const showRecentItemsTab =
    enableTabbedMenuBarRecents && enableRecentItems && recentItemsAvailable;

  const { serviceMode } = useServiceModeContext();
  const { activeDayParts: currentActiveDayParts } = useDayPartContext();
  const {
    getMainMenu,
    getPricingAndAvailability,
    getDayPart,
    showStaticMenu,
    showBurgersForBreakfast,
    data,
    loading,
    isStaticMenu,
  } = useMenuContext();

  const { region } = useLocale();
  const { store } = useStoreContext();
  const { menuId } = useFeatureMenu();

  useEffect(() => {
    getMainMenu(menuId, region, store?.number);
  }, [getMainMenu, menuId, region, store]);

  const mainMenuBaseItems: IMainMenuViewOption[] =
    (data?.Menu?.options?.filter(item => {
      if (isStaticMenu) {
        return true;
      }
      if (item && item.__typename === 'Combo') {
        return false;
      }

      // @ts-ignore
      return getPricingAndAvailability(item?._id)?.isAvailable;
    }) as IMainMenuViewOption[]) || [];

  const { currentDayPart } = getDayPart({ showStaticMenu });

  const menuItems = getTabbedMenuData({
    mainMenuItems: mainMenuBaseItems,
    dayPartMenu: dayPartMenu ?? currentDayPart,
    activeDayParts: activeDayParts ?? currentActiveDayParts,
    showBurgersForBreakfast,
    serviceMode,
    showStaticMenu,
    enableDaypartsMergedMenu,
  });

  const tabbedMenuData = menuItems.map((item: IMainMenuSectionView) => ({
    tabId: item._id,
    label: item.name?.locale,
    to: getMenuOptionUrl(item),
    image: item.image,
    imageDescription: item.imageDescription?.locale,
    carouselImage: item.carouselImage,
  }));

  const fullMenuLabelKey = 'fullMenu';
  const recentsLabelKey = 'recents';
  const favoriteLabelKey = 'favorite';

  const fullMenu = !enabledDirectAccessToMenu
    ? {
        tabId: fullMenuLabelKey,
        label: formatMessage({ id: fullMenuLabelKey }),
        to: routes.menu,
      }
    : null;

  const recentItems = showRecentItemsTab
    ? {
        tabId: recentsLabelKey,
        label: formatMessage({ id: recentsLabelKey }),
        to: `${routes.menu}/${relativeRoutes.recentItems}`,
      }
    : null;

  const favorites =
    enableTabbedMenuBarFavorites && !enabledDirectAccessToMenu
      ? {
          tabId: favoriteLabelKey,
          label: formatMessage({ id: favoriteLabelKey }),
          to: routes.favorites,
        }
      : null;

  const baseItems = [fullMenu, recentItems, favorites];
  const tabbedHeaderItems = concatValidItems<ITabbedMenuData>(baseItems, tabbedMenuData);

  const [activeTab, setActiveTab] = useState(() => {
    return tabbedHeaderItems.findIndex(item => item.to === location.pathname);
  });

  useEffectOnce(() => {
    const isOnMainMenu = location.pathname === routes.menu;

    const idx = tabbedHeaderItems.findIndex(item => item.to === location.pathname);
    const active = idx === -1 && isOnMainMenu ? 0 : idx;

    setActiveTab(active);
  });

  const setAsActiveTab = (tabId: string) => {
    const tabIndex = tabbedHeaderItems.findIndex(item => item.tabId === tabId);
    setActiveTab(tabIndex);
  };

  // check if the current path is recentItems or favorites.
  // If it is and its disabled then redirect to the root menu
  if (
    (!recentItems && isRecentItemsRoute(location.pathname)) ||
    (!favorites && isFavoritesRoute(location.pathname))
  ) {
    navigate(routes.menu);
  }

  const isRouteOnItemPickerAndOutsideTabbedItems =
    isItemPickerOrComboRoute(location.pathname) && activeTab === -1;

  return {
    activeTab,
    setAsActiveTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading: !data || loading,
    tabbedHeaderItems,
  };
};
